import type { PublicLanguage } from "./i18n";
import { dogGroomingGuideCopy } from "./dog-grooming-guide-copy";
import { ArrowRight, CalendarDays, Check, Clock3, Dog, GraduationCap, Languages, ShieldCheck, Wrench } from "lucide-react";

export const dogGroomingGuideUrl = "https://docs.google.com/document/d/1ZgDHwlQ4eURrbLs5UscUkLVP_XcxpNJQiB07wNIvljU";

export function DogGroomingProspectGuide({ language = "en" }: { language?: PublicLanguage }) {
  const text = dogGroomingGuideCopy[language];
  const levelOneTopics = [
    [text.safetyTopic, text.safetyTopicBody],
    [text.behaviorTopic, text.behaviorTopicBody],
    [text.anatomyTopic, text.anatomyTopicBody],
    [text.bathingTopic, text.bathingTopicBody],
    [text.brushingTopic, text.brushingTopicBody],
    [text.dryingTopic, text.dryingTopicBody],
    [text.nailTopic, text.nailTopicBody],
    [text.intakeTopic, text.intakeTopicBody],
  ] as const;

  const levelTwoTopics = [
    text.advancedSafety,
    text.terminology,
    text.equipment,
    text.clipperWork,
    text.shearTechniques,
    text.breedStyling,
    text.advancedCoatCare,
    text.finishGrooming,
    text.businessSkills,
    text.salonPracticum,
  ] as const;

  const careerSettings = [
    text.salons,
    text.boarding,
    text.mobileGrooming,
    text.relatedSettings,
    text.ownBusiness,
  ] as const;

  return (
    <section className="dog-guide" aria-labelledby="dog-guide-title">
      <header className="dog-guide-intro">
        <div>
          <p className="eyebrow">{text.eyebrow}</p>
          <h2 id="dog-guide-title">{text.title}</h2>
          <p>{text.introduction}</p>
        </div>
        <a className="button outline" href={dogGroomingGuideUrl} target="_blank" rel="noreferrer">{text.sourceGuide}<ArrowRight aria-hidden="true" />
        </a>
      </header>

      <div className="dog-guide-stats" aria-label={text.glanceLabel}>
        <article><Clock3 aria-hidden="true" /><strong>{text.totalHoursValue}</strong><span>{text.totalHours}</span></article>
        <article><GraduationCap aria-hidden="true" /><strong>{text.handsOnValue}</strong><span>{text.handsOn}</span></article>
        <article><Dog aria-hidden="true" /><strong>{text.dogsValue}</strong><span>{text.dogsSchedule}</span></article>
        <article><CalendarDays aria-hidden="true" /><strong>{text.daysValue}</strong><span>{text.attendanceChoice}</span></article>
      </div>

      <section className="dog-guide-objective" aria-labelledby="dog-objective-title">
        <div>
          <p className="eyebrow light">{text.objectiveEyebrow}</p>
          <h3 id="dog-objective-title">{text.objectiveTitle}</h3>
        </div>
        <div>
          <p>{text.objectiveBody}</p>
          <p>{text.professionalLimits}</p>
        </div>
      </section>

      <section className="dog-guide-section" aria-labelledby="dog-curriculum-title">
        <header>
          <p className="eyebrow">{text.curriculumEyebrow}</p>
          <h3 id="dog-curriculum-title">{text.curriculumTitle}</h3>
          <p>{text.curriculumBody}</p>
        </header>
        <div className="dog-curriculum-grid">
          <article>
            <div className="dog-level-heading"><span>{text.levelOne}</span><strong>{text.levelOneHours}</strong></div>
            <h4>{text.levelOneTitle}</h4>
            <p>{text.levelOneBody}</p>
            <ul>{levelOneTopics.map(([title, body]) => <li key={title}><Check aria-hidden="true" /><span><strong>{title}</strong><small>{body}</small></span></li>)}</ul>
          </article>
          <article>
            <div className="dog-level-heading"><span>{text.levelTwo}</span><strong>{text.levelTwoHours}</strong></div>
            <h4>{text.levelTwoTitle}</h4>
            <p>{text.levelTwoBody}</p>
            <ul>{levelTwoTopics.map(topic => <li key={topic}><Check aria-hidden="true" /><span>{topic}</span></li>)}</ul>
          </article>
        </div>
        <aside className="dog-practice-note"><Dog aria-hidden="true" /><div><strong>{text.practiceTitle}</strong><p>{text.practiceBody}</p></div></aside>
      </section>

      <section className="dog-guide-schedule" aria-labelledby="dog-schedule-title">
        <div>
          <p className="eyebrow light">{text.scheduleEyebrow}</p>
          <h3 id="dog-schedule-title">{text.scheduleTitle}</h3>
          <p>{text.scheduleBody}</p>
        </div>
        <div className="dog-schedule-card">
          <dl>
            <div><dt>{text.tuesdaySaturday}</dt><dd>{text.weekdayHours}</dd></div>
            <div><dt>{text.sunday}</dt><dd>{text.sundayHours}</dd></div>
            <div><dt>{text.monday}</dt><dd>{text.closed}</dd></div>
            <div><dt>{text.someDays}</dt><dd>{text.extendedHours}</dd></div>
          </dl>
          <p><strong>{text.acceleratedLabel}</strong>{" "}{text.acceleratedBody}</p>
          <p><strong>{text.partTimeLabel}</strong>{" "}{text.partTimeBody}</p>
          <small>{text.completionNotice}</small>
        </div>
      </section>

      <section className="dog-guide-section dog-guide-experience" aria-labelledby="dog-experience-title">
        <header>
          <p className="eyebrow">{text.experienceEyebrow}</p>
          <h3 id="dog-experience-title">{text.experienceTitle}</h3>
        </header>
        <div className="dog-experience-grid">
          <article><Dog aria-hidden="true" /><h4>{text.twoDogsTitle}</h4><p>{text.twoDogsBody}</p></article>
          <article><Languages aria-hidden="true" /><h4>{text.languageSupportTitle}</h4><p>{text.languageSupportBody}</p></article>
          <article><GraduationCap aria-hidden="true" /><h4>{text.competencyTitle}</h4><p>{text.competencyBody}</p></article>
        </div>
      </section>

      <section className="dog-guide-investment" aria-labelledby="dog-investment-title">
        <div>
          <p className="eyebrow light">{text.investmentEyebrow}</p>
          <h3 id="dog-investment-title">{text.investmentTitle}</h3>
        </div>
        <div className="dog-investment-cards">
          <article><strong>{text.tuitionValue}</strong><span>{text.tuitionLabel}</span><p>{text.tuitionBody}</p></article>
          <article><strong>{text.scholarshipValue}</strong><span>{text.scholarshipLabel}</span><p>{text.scholarshipBody}</p></article>
          <article><Wrench aria-hidden="true" /><strong>{text.toolsValue}</strong><span>{text.toolsLabel}</span><p>{text.toolsBody}</p></article>
        </div>
      </section>

      <section className="dog-guide-section dog-guide-fit" aria-labelledby="dog-fit-title">
        <header>
          <p className="eyebrow">{text.fitEyebrow}</p>
          <h3 id="dog-fit-title">{text.fitTitle}</h3>
          <p>{text.fitBody}</p>
        </header>
        <div>
          <article>
            <h4>{text.realitiesTitle}</h4>
            <ul>
              <li><Check aria-hidden="true" />{text.cleanWorkArea}</li>
              <li><Check aria-hidden="true" />{text.liftingStanding}</li>
              <li><Check aria-hidden="true" />{text.patientHandling}</li>
              <li><Check aria-hidden="true" />{text.maintainEquipment}</li>
              <li><Check aria-hidden="true" />{text.communication}</li>
            </ul>
          </article>
          <article>
            <h4>{text.careerTitle}</h4>
            <ul>{careerSettings.map(setting => <li key={setting}><Check aria-hidden="true" />{setting}</li>)}</ul>
            <small>{text.careerNotice}</small>
          </article>
        </div>
      </section>

      <section className="dog-guide-safety" aria-labelledby="dog-safety-title">
        <ShieldCheck aria-hidden="true" />
        <div>
          <p className="eyebrow">{text.safetyEyebrow}</p>
          <h3 id="dog-safety-title">{text.safetyTitle}</h3>
          <p>{text.safetyBody}</p>
          <p>{text.vaccinationNotice}</p>
        </div>
      </section>

      <footer className="dog-guide-footer">
        <div>
          <p className="eyebrow light">{text.footerEyebrow}</p>
          <h3>{text.footerTitle}</h3>
          <p>{text.footerBody}</p>
        </div>
        <a className="button primary" href="#course-enrollment">{text.enrollmentCta}<ArrowRight aria-hidden="true" /></a>
        <small>{text.officialNotice}</small>
      </footer>
    </section>
  );
}
